import { DefaultLanguage, JOURNEY_STEP_TYPES } from "../../../../../constants/Constants";
import PropTypes from 'prop-types';

/* A URL inside a step description: either an explicit http(s) scheme or a "www." prefix. A bare
   domain is deliberately not matched, so text already saved in live journeys - "Node.js", "etc.",
   "v2.0" - keeps rendering as plain text. The match must end on a character that is not sentence
   punctuation, which keeps the full stop out of "see https://blitzz.co." */
const URL_IN_DESCRIPTION = /(?:https?:\/\/|www\.)[^\s<]*[^\s<.,;:!?)\]}'"]/gi;

/* Descriptions are stored as plain text, so the link is built at render time: the text is split
   into plain pieces and anchor elements. A description with no URL is returned untouched, so
   nothing about existing responses changes. */
const linkifyDescription = (description) => {
    if (!description || typeof description !== 'string') {
        return description;
    }

    const parts = [];
    let lastIndex = 0;
    let match;

    URL_IN_DESCRIPTION.lastIndex = 0;
    while ((match = URL_IN_DESCRIPTION.exec(description)) !== null) {
        const url = match[0];

        if (match.index > lastIndex) {
            parts.push(description.slice(lastIndex, match.index));
        }

        /* "www.blitzz.co" is a URL to a person but not to the browser, which would read it as a
           path inside the portal. */
        const href = /^www\./i.test(url) ? 'https://' + url : url;

        /* "text-dec-none" is the shared class this portal puts on an inline link: no underline
           until hover. */
        parts.push(
            <a key={'description-link-' + match.index} className="text-dec-none" href={href} target="_blank" rel="noopener noreferrer">
                {url}
            </a>
        );

        lastIndex = match.index + url.length;
    }

    if (parts.length === 0) {
        return description;
    }

    if (lastIndex < description.length) {
        parts.push(description.slice(lastIndex));
    }

    return parts;
}

const StepDescription = ({ dataObj, currentLanguage }) => {
    if (dataObj.props && dataObj.props.description && (dataObj.props.description[currentLanguage] !== '' || dataObj.props.description[DefaultLanguage] !== '')) {
        const isImageWithTextOrTextOnly = dataObj.type === JOURNEY_STEP_TYPES.IMAGE_WITH_TEXT || dataObj.type === JOURNEY_STEP_TYPES.TEXT_ONLY;
        /* The step keeps one description per language; fall back to the default language when the
           journey was never translated into the one the agent is reading in. */
        const descriptionText = dataObj.props.description[currentLanguage] || dataObj.props.description[DefaultLanguage];

        return (
            <div className={isImageWithTextOrTextOnly ? 'step-info' : 'step-desc'}>
                {descriptionText && (
                    <p>{linkifyDescription(descriptionText)}</p>
                )}
            </div>
        );
    }
    return null;
};

StepDescription.propTypes = {
    dataObj: PropTypes.object,
    currentLanguage: PropTypes.string
}

export default StepDescription;
